import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight analytics event tracker.
 * Fire-and-forget — never blocks UI.
 */
export async function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Use rpc or direct insert — the RLS policy handles org scoping
    const orgId = await getOrgId(user.id);
    if (!orgId) return;

    await supabase.from("analytics_events").insert({
      user_id: user.id,
      org_id: orgId,
      event_name: eventName,
      properties,
    } as any);
  } catch {
    // Silent fail — analytics should never break the app
  }
}

let cachedOrgId: string | null = null;

async function getOrgId(userId: string): Promise<string | null> {
  if (cachedOrgId) return cachedOrgId;
  const { data } = await supabase.rpc("get_user_org_id", { _user_id: userId });
  cachedOrgId = data;
  return data;
}

// ── Common event names ──────────────────────────────────────────
export const EVENTS = {
  // Auth
  SIGN_UP: "sign_up",
  SIGN_IN: "sign_in",
  // Navigation
  PAGE_VIEW: "page_view",
  // Features
  CHAT_MESSAGE: "chat_message_sent",
  DOCUMENT_UPLOADED: "document_uploaded",
  DEVICE_DISCOVERED: "device_discovered",
  CVE_SEARCHED: "cve_searched",
  COMMAND_OUTPUT_ADDED: "command_output_added",
  CONFIG_SNAPSHOT_SAVED: "config_snapshot_saved",
  INVITE_SENT: "invite_sent",
  MONITORING_CONNECTED: "monitoring_connected",
  DISCOVERY_RUN: "discovery_run_started",
  EXPORT_CSV: "csv_exported",
} as const;
